import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { billingApi } from '@/api/ops';
import type { BillingSettings, CostCard, CostCardVocabulary, PriceBookItem, PricingRule } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { CheckboxField, ReasonField, TextAreaField, TextField, reasonOk } from '@/components/ops/Forms';
import { KeyValues, PageHeader, Section, SubTabs } from '@/components/ops/Layout';
import { ConfirmDialog, Modal } from '@/components/ops/Modal';
import { EnvironmentPicker } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { displayDecimals, formatCredits, isValidScale, parseDisplayedCredits, scaleText } from '@/ops/credits';
import { formatBps, formatMinor, formatTimestamp, localInputToIso, minorToMajorText, parseMajorToMinor, shortId } from '@/ops/format';
import { LIVE } from '@/ops/live';
import { scaleUnavailableReason, useCreditScale } from '@/ops/useCreditScale';
import { useSelectedEnvironment } from '@/ops/useEnvironment';
import { AiRatesPanel } from './AiRatesPanel';
import { MessagePricesPanel } from './MessagePrices';

type Tab = 'messages' | 'book' | 'settings' | 'fx' | 'rules' | 'cost-cards' | 'ai';

const k = (env: string, ...rest: string[]) => ['ops', 'billing', env, ...rest] as const;

/** The SKU families of the price book (CONTRACTS §4.3), in the order the page lists them. */
export function skuFamily(sku: string): string {
  if (sku.startsWith('plan.')) return 'Plans';
  if (sku.startsWith('credits.pack.')) return 'Credit packs';
  if (sku.startsWith('credits.unit.')) return 'Credit unit prices';
  if (sku.startsWith('sms.')) return 'SMS rates (credits)';
  if (sku.startsWith('whatsapp.')) return 'WhatsApp rates (credits)';
  if (sku.startsWith('storage.overage') || sku.startsWith('seat.overage')) return 'Overages';
  if (sku.startsWith('payments.')) return 'Payments fee';
  return 'Other';
}

const FAMILY_ORDER = ['Plans', 'Credit packs', 'Credit unit prices', 'SMS rates (credits)', 'WhatsApp rates (credits)', 'Overages', 'Payments fee', 'Other'];

export function groupBySkuFamily<T extends { sku: string }>(items: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const family = skuFamily(item.sku);
    groups.set(family, [...(groups.get(family) ?? []), item]);
  }
  return FAMILY_ORDER.filter((f) => groups.has(f)).map((f) => [f, groups.get(f)!.sort((a, b) => a.sku.localeCompare(b.sku))]);
}

/** Billing accepts any whole display scale in this range (2026-10-14; was 1/10/…/100000). */
export const MAX_DISPLAY_SCALE = 1_000_000;

/** An amount for its currency: money for an ISO code, credits for `CREDITS` (micro-credits). */

function amountText(item: { currency: string; amount: number }, scale: number | null): string {
  return item.currency === 'CREDITS' ? formatCredits(item.amount, scale) : formatMinor(item.amount, item.currency);
}

/**
 * Pricing (plan §6.6, D-CP15/D-CP21): the versioned price book (never edited once published — a new
 * version is published with an effective-from), the platform billing settings incl. the credit display
 * scale, FX rates, the default markups and the add-only cost cards. Everything live from billing.
 */
export function PricingPage() {
  const { env, setEnv, environments } = useSelectedEnvironment();
  const [params, setParams] = useSearchParams();
  // Costs, markups and the price book (whose generated rows carry cost and margin) are costs:read; a role without it sees
  // the settings and FX rates only (review 2026-10-01).
  const seesCosts = hasPermission(PERMISSIONS.costsRead);
  const requested = params.get('tab') as Tab | null;
  const costTabs: Tab[] = ['messages', 'book', 'rules', 'cost-cards', 'ai'];
  const tab: Tab = requested && (seesCosts || !costTabs.includes(requested)) ? requested : seesCosts ? 'book' : 'settings';
  const setTab = (t: Tab) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set('tab', t);
    next.delete('version');
    return next;
  }, { replace: true });

  return (
    <RequirePermission permission={PERMISSIONS.billingRead} what="Pricing">
      <PageHeader title="Pricing" subtitle="Prices are data the admin edits — versioned, never changed in place."
        actions={<EnvironmentPicker env={env} environments={environments} onChange={setEnv} />} />
      <SubTabs<Tab> label="Pricing sections" active={tab} onChange={setTab} tabs={[
        { key: 'messages', label: 'Message prices', hidden: !seesCosts },
        { key: 'book', label: 'Price book', hidden: !seesCosts },
        { key: 'settings', label: 'Billing settings' },
        { key: 'fx', label: 'FX rates' },
        { key: 'rules', label: 'Pricing rules', hidden: !seesCosts },
        { key: 'cost-cards', label: 'Cost cards', hidden: !seesCosts },
        { key: 'ai', label: 'AI', hidden: !seesCosts },
      ]} />
      {env && tab === 'messages' && <MessagePricesPanel env={env} />}
      {env && tab === 'book' && <PriceBook env={env} />}
      {env && tab === 'settings' && <SettingsPanel env={env} />}
      {env && tab === 'fx' && <FxRatesPanel env={env} />}
      {env && tab === 'rules' && <PricingRulesPanel env={env} />}
      {env && tab === 'cost-cards' && <CostCardsPanel env={env} />}
      {env && tab === 'ai' && <AiRatesPanel env={env} />}
    </RequirePermission>
  );
}

// ─── Price book ───────────────────────────────────────────────────────────

function PriceBook({ env }: { env: string }) {
  const [params, setParams] = useSearchParams();
  const versionId = params.get('version');
  const open = (id: string | null) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (id) next.set('version', id);
    else next.delete('version');
    return next;
  });

  return versionId
    ? <VersionDetail env={env} id={versionId} onBack={() => open(null)} />
    : <VersionsList env={env} onOpen={open} />;
}

function VersionsList({ env, onOpen }: { env: string; onOpen: (id: string) => void }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const versions = useQuery({ queryKey: k(env, 'versions'), queryFn: () => billingApi.versions(env), ...LIVE });
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [diff, setDiff] = useState<{ from: string; to: string }>({ from: '', to: '' });

  const create = useMutation({
    mutationFn: () => billingApi.createDraft(env, { note: note.trim() || undefined }),
    onSuccess: (detail) => {
      setCreating(false);
      setNote('');
      queryClient.invalidateQueries({ queryKey: k(env, 'versions') });
      onOpen(detail.version.id);
    },
  });

  return (
    <QueryState query={versions} service="billing">
      {(data) => {
        const list = [...data.versions].sort((a, b) => b.created_at.localeCompare(a.created_at));
        return (
          <>
            <Section title="Versions" meta="the newest published version whose effective-from has passed is in force"
              actions={canManage && <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>New draft (copy of current)</button>} flush>
              {list.length === 0 ? (
                <div className="empty-state t-body">No price-book versions.</div>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Version</th><th>Status</th><th>Effective from</th><th className="num">Items</th><th>Created</th><th>Published</th></tr></thead>
                    <tbody>
                      {list.map((v) => (
                        <tr key={v.id} className="row-clickable" onClick={() => onOpen(v.id)}>
                          <td>
                            <button type="button" className="linklike mono" onClick={() => onOpen(v.id)}>{shortId(v.id)}</button>
                            {v.note && <div className="t-caption muted">{v.note}</div>}
                          </td>
                          <td className="row"><StatusChip status={v.status} />{v.in_force && <StatusChip status="in force" tone="success" />}</td>
                          <td className="t-caption">{v.effective_from ? formatTimestamp(v.effective_from) : '—'}</td>
                          <td className="num">{v.item_count}</td>
                          <td className="t-caption">{formatTimestamp(v.created_at)}<div className="muted">{v.created_by ?? ''}</div></td>
                          <td className="t-caption">{v.published_at ? formatTimestamp(v.published_at) : '—'}<div className="muted">{v.published_by ?? ''}</div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            <Section title="Compare two versions">
              <div className="row-end">
                <div style={{ width: 260 }}>
                  <Select label="From" value={diff.from} onChange={(from) => setDiff({ ...diff, from })} placeholder="Choose"
                    options={list.map((v) => ({ value: v.id, label: `${shortId(v.id)} · ${v.status}`, hint: v.in_force ? 'in force' : undefined }))} />
                </div>
                <div style={{ width: 260 }}>
                  <Select label="To" value={diff.to} onChange={(to) => setDiff({ ...diff, to })} placeholder="Choose"
                    options={list.map((v) => ({ value: v.id, label: `${shortId(v.id)} · ${v.status}`, hint: v.in_force ? 'in force' : undefined }))} />
                </div>
              </div>
              {diff.from && diff.to && diff.from !== diff.to && <DiffView env={env} from={diff.from} to={diff.to} />}
            </Section>

            {creating && (
              <Modal title="New draft" onClose={create.isPending ? undefined : () => setCreating(false)} dismissible={!create.isPending}
                footer={
                  <>
                    <button type="button" className="btn btn-ghost" onClick={() => setCreating(false)} disabled={create.isPending}>Cancel</button>
                    <button type="button" className="btn btn-primary" disabled={create.isPending} onClick={() => create.mutate()}>
                      {create.isPending ? 'Creating…' : 'Create draft'}
                    </button>
                  </>
                }>
                <p className="t-body" style={{ margin: 0 }}>A draft copies every item of the current published version. Edit it, then publish it with an effective-from.</p>
                <TextField label="Note" value={note} onChange={setNote} placeholder="Q4 SMS repricing" />
                <MutationError error={create.error} service="billing" />
              </Modal>
            )}
          </>
        );
      }}
    </QueryState>
  );
}

function DiffView({ env, from, to }: { env: string; from: string; to: string }) {
  const diff = useQuery({ queryKey: k(env, 'diff', from, to), queryFn: () => billingApi.diff(env, from, to), ...LIVE });
  const { scale } = useCreditScale(env);
  return (
    <div style={{ marginTop: 16 }}>
      <QueryState query={diff} service="billing">
        {(d) => (
          d.added.length + d.removed.length + d.changed.length === 0 ? (
            <div className="empty-state t-body">The two versions carry identical prices.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Change</th><th>SKU</th><th className="num">From</th><th className="num">To</th><th>Metadata</th></tr></thead>
                <tbody>
                  {d.added.map((i) => (
                    <tr key={`a-${i.sku}-${i.currency}`}>
                      <td><StatusChip status="added" tone="success" /></td><td className="mono">{i.sku}</td><td className="num">—</td>
                      <td className="num">{amountText(i, scale)}</td>
                      <td><MetadataChanges changes={metadataChanges(null, i.metadata)} scale={scale} /></td>
                    </tr>
                  ))}
                  {d.removed.map((i) => (
                    <tr key={`r-${i.sku}-${i.currency}`}>
                      <td><StatusChip status="removed" tone="danger" /></td><td className="mono">{i.sku}</td>
                      <td className="num">{amountText(i, scale)}</td><td className="num">—</td>
                      <td><MetadataChanges changes={metadataChanges(i.metadata, null)} scale={scale} /></td>
                    </tr>
                  ))}
                  {d.changed.map((c) => (
                    <tr key={`c-${c.sku}-${c.currency}`} className="row-changed">
                      <td><StatusChip status="changed" tone="info" /></td>
                      <td className="mono">{c.sku}</td>
                      <td className="num">{c.from_amount === c.to_amount ? <span className="muted">{amountText({ currency: c.currency, amount: c.from_amount }, scale)}</span> : amountText({ currency: c.currency, amount: c.from_amount }, scale)}</td>
                      <td className="num">{c.from_amount === c.to_amount ? <span className="muted">same</span> : amountText({ currency: c.currency, amount: c.to_amount }, scale)}</td>
                      <td><MetadataChanges changes={metadataChanges(c.from_metadata, c.to_metadata)} scale={scale} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </QueryState>
    </div>
  );
}

/** One metadata key that differs between two price-book rows (`null` = absent on that side). */
export interface MetadataChange {
  key: string;
  from: unknown;
  to: unknown;
}

/** The keys whose values differ (deep, by JSON), sorted — what a price-book diff must show beside the amounts. */
export function metadataChanges(from: Record<string, unknown> | null | undefined, to: Record<string, unknown> | null | undefined): MetadataChange[] {
  const a = from ?? {};
  const b = to ?? {};
  const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])].sort();
  return keys
    .filter((key) => JSON.stringify(a[key] ?? null) !== JSON.stringify(b[key] ?? null))
    .map((key) => ({ key, from: a[key] ?? null, to: b[key] ?? null }));
}

function metadataValueText(key: string, value: unknown, scale: number | null): string {
  if (value === null || value === undefined) return '—';
  // A pack's grant is micro-credits on the wire: shown in displayed credits like every other credit figure.
  if (key === 'credits_granted' && typeof value === 'number') return formatCredits(value, scale);
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function MetadataChanges({ changes, scale }: { changes: MetadataChange[]; scale: number | null }) {
  if (changes.length === 0) return <span className="t-caption muted">—</span>;
  return (
    <ul className="t-caption" style={{ margin: 0, paddingInlineStart: 16 }}>
      {changes.map((c) => (
        <li key={c.key}>
          <span className="mono">{c.key}</span>: {metadataValueText(c.key, c.from, scale)} → <strong>{metadataValueText(c.key, c.to, scale)}</strong>
        </li>
      ))}
    </ul>
  );
}

interface EditableItem {
  item: PriceBookItem;
  /** The amount as the admin sees and types it: major units, or displayed credits. */
  text: string;
  /** The metadata JSON WITHOUT a credit pack's `credits_granted` (that one is typed in displayed credits). */
  metadataText: string;
  /** A credit pack's grant, typed in DISPLAYED credits; converted to micro-credits on save. Undefined for other SKUs. */
  grantedText?: string;
}

const isCreditPack = (sku: string) => sku.startsWith('credits.pack.');

/** The wallets a custom credit purchase can buy into. */
export const CREDIT_UNIT_WALLETS = ['ai', 'communications'] as const;

/**
 * A `credits.unit.<wallet>` row is the price of ONE displayed credit for a custom-amount purchase: unit `credit`, an ISO
 * currency (never CREDITS), amount in that currency's minor units (100 = 1.00 EGP). Null when the row is well-formed or
 * is not a unit row.
 */
export function creditUnitProblem(item: { sku: string; currency: string; unit: string }): string | null {
  if (!item.sku.startsWith('credits.unit.')) return null;
  const wallet = item.sku.slice('credits.unit.'.length);
  if (!(CREDIT_UNIT_WALLETS as readonly string[]).includes(wallet)) return `the wallet must be one of ${CREDIT_UNIT_WALLETS.join(', ')}.`;
  if (item.unit !== 'credit') return 'a credit unit price is per "credit".';
  if (!/^[A-Z]{3}$/.test(item.currency)) return 'a credit unit price is money — an ISO currency, not CREDITS.';
  return null;
}

/** The helper line under a credit unit row. */
export const CREDIT_UNIT_HINT = 'credits.unit.ai = price of 1 credit (minor units: 100 = 1.00 EGP). It enables custom amounts; credits.unit.communications does the same for SMS & WhatsApp.';

function toEditable(item: PriceBookItem, scale: number | null): EditableItem {
  const text = item.currency === 'CREDITS'
    ? isValidScale(scale) ? (item.amount / scale).toFixed(displayDecimals(scale)) : ''
    : minorToMajorText(item.amount, item.currency);
  if (!isCreditPack(item.sku)) return { item, text, metadataText: item.metadata ? JSON.stringify(item.metadata) : '' };

  const { credits_granted: granted, ...rest } = item.metadata ?? {};
  return {
    item,
    text,
    metadataText: Object.keys(rest).length > 0 ? JSON.stringify(rest) : '',
    grantedText: typeof granted === 'number' && isValidScale(scale)
      ? String(granted / scale)
      : '',
  };
}

/**
 * A credit pack's grant typed in displayed credits → micro-credits (what billing stores in `credits_granted`), with the
 * preview line the form shows. Never converted with a guessed scale.
 */
export function grantedPreview(text: string, scale: number | null): { ok: true; micro: number; preview: string } | { ok: false; error: string } {
  if (text.trim() === '') return { ok: false, error: 'A credit pack must say how many credits it grants.' };
  const parsed = parseDisplayedCredits(text, scale);
  if (!parsed.ok) return parsed;
  if (parsed.micro <= 0) return { ok: false, error: 'A credit pack must grant more than 0 credits.' };
  return { ok: true, micro: parsed.micro, preview: `${formatCredits(parsed.micro, scale)} = ${parsed.micro.toLocaleString('en-US')} micro-credits` };
}

/** Converts the edited rows back to wire items; the first problem stops the save. */
export function editedItems(rows: EditableItem[], scale: number | null): { items: PriceBookItem[]; error: string | null } {
  const items: PriceBookItem[] = [];
  for (const row of rows) {
    const parsed = row.item.currency === 'CREDITS'
      ? parseDisplayedCredits(row.text, scale)
      : parseMajorToMinor(row.text, row.item.currency);
    if (!parsed.ok) return { items: [], error: `${row.item.sku}: ${parsed.error}` };
    const unitProblem = creditUnitProblem(row.item);
    if (unitProblem) return { items: [], error: `${row.item.sku}: ${unitProblem}` };
    let metadata: Record<string, unknown> | null = null;
    if (row.metadataText.trim()) {
      try {
        metadata = JSON.parse(row.metadataText) as Record<string, unknown>;
      } catch {
        return { items: [], error: `${row.item.sku}: metadata is not valid JSON.` };
      }
    }
    if (row.grantedText !== undefined) {
      const granted = grantedPreview(row.grantedText, scale);
      if (!granted.ok) return { items: [], error: `${row.item.sku}: ${granted.error}` };
      metadata = { ...(metadata ?? {}), credits_granted: granted.micro };
    }
    items.push({ ...row.item, amount: 'micro' in parsed ? parsed.micro : parsed.minor, metadata });
  }
  return { items, error: null };
}

function VersionDetail({ env, id, onBack }: { env: string; id: string; onBack: () => void }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const detail = useQuery({ queryKey: k(env, 'version', id), queryFn: () => billingApi.version(env, id), ...LIVE });
  const scale = useCreditScale(env);
  const [rows, setRows] = useState<EditableItem[] | null>(null);
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState<'generate' | 'publish' | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [publishReason, setPublishReason] = useState('');

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: k(env, 'version', id) });
    queryClient.invalidateQueries({ queryKey: k(env, 'versions') });
  };

  const edited = rows ? editedItems(rows, scale.scale) : null;
  const save = useMutation({
    mutationFn: () => billingApi.replaceItems(env, id, edited!.items, reason.trim()),
    onSuccess: () => { setRows(null); setReason(''); refresh(); },
  });
  const generate = useMutation({
    mutationFn: () => billingApi.generateConsumption(env, id),
    onSuccess: () => { setConfirm(null); setRows(null); refresh(); },
  });
  const effectiveIso = localInputToIso(effectiveFrom);
  const publish = useMutation({
    mutationFn: () => billingApi.publish(env, id, effectiveIso!, publishReason.trim()),
    onSuccess: () => { setConfirm(null); setPublishReason(''); refresh(); },
  });

  return (
    <QueryState query={detail} service="billing">
      {({ version: v, prices }) => {
        const draft = v.status === 'draft';
        const editing = rows !== null;
        const display = editing ? rows!.map((r) => r.item) : prices;
        const groups = groupBySkuFamily(display.map((item, index) => ({ ...item, index })));
        const creditsBlocked = scale.status !== 'ok';

        return (
          <>
            <Section
              title={<>Version <span className="mono">{shortId(v.id)}</span></>}
              meta={v.note ?? undefined}
              actions={
                <>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>← All versions</button>
                  {draft && canManage && !editing && (
                    <>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRows(prices.map((p) => toEditable(p, scale.scale)))}>Edit items</button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirm('generate')}>Generate SMS/WhatsApp prices from cost × markup</button>
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => setConfirm('publish')}>Publish…</button>
                    </>
                  )}
                </>
              }
            >
              <KeyValues rows={[
                { label: 'Status', value: <span className="row"><StatusChip status={v.status} />{v.in_force && <StatusChip status="in force" tone="success" />}</span> },
                { label: 'Effective from', value: v.effective_from ? formatTimestamp(v.effective_from) : 'set when published' },
                { label: 'Copied from', value: v.copied_from_version_id ? <span className="mono">{shortId(v.copied_from_version_id)}</span> : '—' },
                { label: 'Created', value: `${formatTimestamp(v.created_at)} · ${v.created_by ?? ''}` },
                { label: 'Published', value: v.published_at ? `${formatTimestamp(v.published_at)} · ${v.published_by ?? ''}` : '—' },
              ]} />
              {!draft && (
                <p className="t-caption muted" style={{ margin: '12px 0 0' }}>
                  A published version is never edited. To change a price, create a new draft and publish it with an effective-from.
                </p>
              )}
            </Section>

            <Section title="Items" meta={`${display.length} SKU(s)`} flush>
              {editing && creditsBlocked && (
                <div className="card-body"><span className="t-caption note-error">{scaleUnavailableReason(scale.status)} CREDITS rows cannot be saved until it can.</span></div>
              )}
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>SKU</th><th>Unit</th><th className="num">Price</th><th>Metadata</th>{editing && <th aria-label="Row actions" />}</tr></thead>
                  <tbody>
                    {groups.map(([family, items]) => (
                      <Fragment key={family}>
                        <tr className="group-row"><td colSpan={editing ? 5 : 4}>
                          {family}
                          {family === 'Credit unit prices' && <span className="t-caption muted"> — {CREDIT_UNIT_HINT}</span>}
                        </td></tr>
                        {items.map((item) => (
                          <ItemRow key={`${item.sku}-${item.currency}-${item.index}`} item={item} scale={scale.scale}
                            edit={editing ? rows![item.index]! : null}
                            onChange={(patch) => setRows(rows!.map((r, i) => (i === item.index ? { ...r, ...patch } : r)))}
                            onRemove={() => setRows(rows!.filter((_, i) => i !== item.index))} />
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              {editing && (
                <div className="card-body stack-sm">
                  <div><button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdding(true)}>Add item</button></div>
                  {edited?.error && <span className="t-caption note-error">{edited.error}</span>}
                  <ReasonField value={reason} onChange={setReason} />
                  <div className="form-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => { setRows(null); save.reset(); }} disabled={save.isPending}>Discard edits</button>
                    <button type="button" className="btn btn-primary" disabled={!!edited?.error || !reasonOk(reason) || save.isPending} onClick={() => save.mutate()}>
                      {save.isPending ? 'Saving…' : 'Save draft items'}
                    </button>
                  </div>
                  <MutationError error={save.error} service="billing" />
                </div>
              )}
            </Section>

            {adding && rows && (
              <AddItemForm scale={scale.scale} onClose={() => setAdding(false)}
                onAdd={(row) => { setRows([...rows, row]); setAdding(false); }} />
            )}

            {confirm === 'generate' && (
              <ConfirmDialog title="Generate consumption prices?" confirmLabel="Generate" pending={generate.isPending}
                onConfirm={() => generate.mutate()} onCancel={() => { setConfirm(null); generate.reset(); }}>
                <p className="t-body" style={{ margin: 0 }}>
                  Fills this draft's SMS and WhatsApp CREDITS rows from the effective cost cards × the pricing rules' markup
                  (price = ceil(cost × (1 + markup))). Review and hand-edit before publishing. Unsaved edits are discarded.
                </p>
                <MutationError error={generate.error} service="billing" />
              </ConfirmDialog>
            )}
            {confirm === 'publish' && (
              <ConfirmDialog title="Publish this version?" confirmLabel="Publish version" destructive pending={publish.isPending}
                confirmDisabled={!effectiveIso || !reasonOk(publishReason)}
                onConfirm={() => publish.mutate()} onCancel={() => { setConfirm(null); publish.reset(); }}>
                <p className="t-body" style={{ margin: 0 }}>
                  Publishing freezes these {prices.length} prices forever. From the effective moment, new invoices, purchases and
                  checkouts price from this version; everything already issued keeps its snapshot.
                </p>
                <TextField label="Effective from (your local time)" type="datetime-local" value={effectiveFrom} onChange={setEffectiveFrom}
                  hint={effectiveIso ? `Sent as ${effectiveIso}` : 'Required.'} />
                <ReasonField value={publishReason} onChange={setPublishReason} />
                <MutationError error={publish.error} service="billing" />
              </ConfirmDialog>
            )}
          </>
        );
      }}
    </QueryState>
  );
}

function ItemRow({
  item, scale, edit, onChange, onRemove,
}: {
  item: PriceBookItem;
  scale: number | null;
  edit: EditableItem | null;
  onChange: (patch: Partial<EditableItem>) => void;
  onRemove: () => void;
}) {
  const isCredits = item.currency === 'CREDITS';
  const meta = item.metadata ?? {};
  const granted = typeof meta.credits_granted === 'number' ? meta.credits_granted : null;
  return (
    <tr>
      <td>
        <span className="mono">{item.sku}</span>
        {typeof meta.label_en === 'string' && <div className="t-caption muted">{meta.label_en}</div>}
      </td>
      <td className="t-caption">{item.unit} · {item.currency}</td>
      <td className="num" style={{ minWidth: 160 }}>
        {edit ? (
          <input className="input input-inline" aria-label={`Price of ${item.sku}`} value={edit.text}
            disabled={isCredits && !isValidScale(scale)} onChange={(e) => onChange({ text: e.target.value })} />
        ) : (
          <>
            {amountText(item, scale)}
            {isCredits && <div className="t-caption muted">{item.amount.toLocaleString('en-US')} µ per {item.unit}</div>}
          </>
        )}
      </td>
      <td className="t-caption">
        {edit ? (
          <>
            {edit.grantedText !== undefined && (
              <GrantedField sku={item.sku} value={edit.grantedText} scale={scale} onChange={(grantedText) => onChange({ grantedText })} />
            )}
            <input className="input input-inline mono" aria-label={`Metadata of ${item.sku}`} value={edit.metadataText}
              onChange={(e) => onChange({ metadataText: e.target.value })} placeholder="{}" />
          </>
        ) : (
          <>
            {granted !== null && <div>grants {formatCredits(granted, scale)}{typeof meta.wallet === 'string' ? ` → ${meta.wallet}` : ''}</div>}
            {Object.keys(meta).filter((key) => !['credits_granted', 'wallet', 'label_en', 'label_ar'].includes(key)).length > 0 && (
              <span className="mono subtle">{JSON.stringify(meta)}</span>
            )}
          </>
        )}
      </td>
      {edit && <td><button type="button" className="btn btn-ghost btn-sm is-danger" onClick={onRemove}>Remove</button></td>}
    </tr>
  );
}

/** A credit pack's grant, typed in displayed credits, with the micro-credit preview (or why it cannot be read). */
function GrantedField({ sku, value, scale, onChange }: { sku: string; value: string; scale: number | null; onChange: (value: string) => void }) {
  const preview = grantedPreview(value, scale);
  return (
    <div style={{ marginBottom: 6 }}>
      <input className="input input-inline" aria-label={`Credits granted by ${sku}`} value={value} inputMode="decimal"
        disabled={!isValidScale(scale)} placeholder="credits granted" onChange={(e) => onChange(e.target.value)} />
      <div className={`t-caption${preview.ok ? ' muted' : ' note-error'}`}>
        {preview.ok ? `Grants ${preview.preview}` : preview.error}
      </div>
    </div>
  );
}

function AddItemForm({ scale, onClose, onAdd }: { scale: number | null; onClose: () => void; onAdd: (row: EditableItem) => void }) {
  const [sku, setSku] = useState('');
  const [currency, setCurrency] = useState('EGP');
  const [unit, setUnit] = useState('month');
  const [amount, setAmount] = useState('');
  const [metadata, setMetadata] = useState('');
  const [granted, setGranted] = useState('');
  const upper = currency.toUpperCase();
  const pack = isCreditPack(sku.trim());
  const creditUnit = sku.trim().startsWith('credits.unit.');
  const row: EditableItem = {
    item: { sku: sku.trim(), currency: upper, unit: unit.trim(), amount: 0, metadata: null },
    text: amount,
    metadataText: metadata,
    grantedText: pack ? granted : undefined,
  };
  const priceCheck = upper === 'CREDITS' ? parseDisplayedCredits(amount, scale) : parseMajorToMinor(amount, upper);
  const grantCheck = grantedPreview(granted, scale);
  const check = editedItems([row], scale);
  const valid = sku.trim() !== '' && unit.trim() !== '' && (upper === 'CREDITS' || /^[A-Z]{3}$/.test(upper)) && !check.error;

  return (
    <Modal title="Add a price-book item" onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid} onClick={() => onAdd(row)}>Add to draft</button>
        </>
      }>
      <div className="form-grid">
        <TextField label="SKU" value={sku} onChange={setSku} mono placeholder="credits.pack.communications.10000"
          hint={creditUnit ? CREDIT_UNIT_HINT : 'credits.unit.ai / credits.unit.communications = the price of 1 credit for custom amounts.'} />
        <TextField label="Currency" value={currency} onChange={setCurrency} mono hint="ISO code, or CREDITS for a consumption rate." />
        <TextField label="Unit" value={unit} onChange={setUnit} mono placeholder="month · pack · part · message · credit"
          error={creditUnit && unit.trim() !== 'credit' ? 'A credit unit price is per "credit".' : null} />
        <TextField label={upper === 'CREDITS' ? 'Price (displayed credits)' : `Price (${upper})`} value={amount} onChange={setAmount}
          error={amount && !priceCheck.ok ? priceCheck.error : null} />
        {pack && (
          <TextField label="Credits granted (displayed credits)" value={granted} onChange={setGranted}
            error={granted && !grantCheck.ok ? grantCheck.error : null}
            hint={grantCheck.ok ? `Grants ${grantCheck.preview}` : 'Typed in displayed credits, stored as micro-credits.'} />
        )}
      </div>
      <TextAreaField label="Metadata (JSON)" value={metadata} onChange={setMetadata} mono rows={3}
        hint={pack
          ? 'The grant is typed above. Other keys: {"wallet": "communications", "label_en": "…", "label_ar": "…"}'
          : 'Optional JSON, e.g. {"label_en": "…", "label_ar": "…"}'} />
    </Modal>
  );
}

// ─── Billing settings ─────────────────────────────────────────────────────

/** Why a typed credit display scale is refused, or null: any whole number from 1 to 1,000,000 (2026-10-14). */
export function scaleProblem(text: string): string | null {
  const value = Number(text.trim());
  if (text.trim() === '' || !Number.isInteger(value) || value < 1 || value > MAX_DISPLAY_SCALE) {
    return `A whole number from 1 to ${MAX_DISPLAY_SCALE.toLocaleString('en-US')}.`;
  }
  return null;
}

export interface PurchaseSettingsInput {
  /** The service fee as a percent (`2` = 2 %, up to two decimals). */
  feePercent: string;
  /** Displayed credits. */
  minCredits: string;
  /** Displayed credits. */
  maxCredits: string;
}

/** The purchase settings as the form first shows them ('' where an older billing does not send the field). */
export function purchaseSettingsInput(settings: BillingSettings): PurchaseSettingsInput {
  const fee = settings.purchase_service_fee_bps;
  return {
    feePercent: typeof fee === 'number' ? String(fee / 100) : '',
    minCredits: typeof settings.min_purchase_credits === 'number' ? String(settings.min_purchase_credits) : '',
    maxCredits: typeof settings.max_purchase_credits === 'number' ? String(settings.max_purchase_credits) : '',
  };
}

/**
 * The purchase-settings part of the settings update: the service fee in BASIS POINTS (a typed 2.5 % → 250), and the
 * min / max credits per purchase in displayed credits — only what changed, with a reason per refused field. An emptied
 * field is refused when billing has a value (never saved as 0); left empty on an older billing it is simply not sent.
 */
export function purchaseSettingsPatch(
  settings: BillingSettings,
  input: PurchaseSettingsInput,
): { patch: Record<string, number>; errors: { fee: string | null; min: string | null; max: string | null } } {
  const patch: Record<string, number> = {};
  const errors = { fee: null as string | null, min: null as string | null, max: null as string | null };

  const fee = input.feePercent.trim();
  if (fee === '') {
    if (typeof settings.purchase_service_fee_bps === 'number') errors.fee = 'Enter a fee (0 for none).';
  } else if (!/^\d+(\.\d{1,2})?$/.test(fee) || Number(fee) > 100) {
    errors.fee = '0–100 %, at most two decimals.';
  } else {
    const bps = Math.round(Number(fee) * 100);
    if (bps !== settings.purchase_service_fee_bps) patch.purchase_service_fee_bps = bps;
  }

  const whole = (text: string, current: number | null | undefined, key: 'min_purchase_credits' | 'max_purchase_credits') => {
    const t = text.trim();
    if (t === '') return { value: current ?? null, error: typeof current === 'number' ? 'Enter a number of credits.' : null };
    if (!/^\d+$/.test(t) || !Number.isSafeInteger(Number(t)) || Number(t) < 1) return { value: null, error: 'A whole number of credits, 1 or more.' };
    const value = Number(t);
    if (value !== current) patch[key] = value;
    return { value, error: null };
  };
  const min = whole(input.minCredits, settings.min_purchase_credits, 'min_purchase_credits');
  const max = whole(input.maxCredits, settings.max_purchase_credits, 'max_purchase_credits');
  errors.min = min.error;
  errors.max = max.error;
  if (!errors.min && !errors.max && min.value !== null && max.value !== null && max.value < min.value) {
    errors.max = 'The maximum must be at least the minimum.';
  }
  return { patch, errors };
}

function SettingsPanel({ env }: { env: string }) {
  const settings = useQuery({ queryKey: k(env, 'settings'), queryFn: () => billingApi.settings(env), ...LIVE });
  const [editing, setEditing] = useState(false);
  const canManage = hasPermission(PERMISSIONS.billingManage);

  return (
    <Section title="Platform billing defaults" meta="a tenant's billing profile overrides currency and tax"
      actions={canManage && settings.data && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>Edit</button>}>
      <QueryState query={settings} service="billing">
        {(s) => (
          <>
            <KeyValues rows={[
              { label: 'Default currency', value: s.default_currency },
              { label: 'Default tax', value: `${s.default_tax_kind} ${formatBps(s.default_tax_rate_bps)} (${s.tax_label_en} / ${s.tax_label_ar})` },
              { label: 'Prices', value: s.prices_ex_tax ? 'ex tax' : 'incl. tax' },
              { label: 'Invoice numbering', value: <span className="mono">{s.invoice_prefix} · next {s.invoice_next_seq}</span> },
              { label: 'Rounding', value: s.rounding },
              {
                label: 'Credit display scale',
                value: <>1 displayed credit = <strong>{scaleText(s.credit_display_scale)}</strong> micro-credits <span className="t-caption muted">(reference {s.credit_reference_currency}: 100,000 micro-credits = 1)</span></>,
              },
              { label: 'Dunning schedule', value: `${s.dunning_schedule_days.join(', ')} days` },
              {
                label: 'Service fee on credit purchases',
                value: typeof s.purchase_service_fee_bps === 'number'
                  ? <>{formatBps(s.purchase_service_fee_bps)} <span className="t-caption muted">of the credits' price after discount, before tax</span></>
                  : <span className="muted">not reported by this billing version</span>,
              },
              {
                label: 'Credits per custom purchase',
                value: typeof s.min_purchase_credits === 'number' && typeof s.max_purchase_credits === 'number'
                  ? `${s.min_purchase_credits.toLocaleString('en-US')} – ${s.max_purchase_credits.toLocaleString('en-US')} credits`
                  : <span className="muted">not reported by this billing version</span>,
              },
              { label: 'Updated', value: `${formatTimestamp(s.updated_at)}${s.updated_by ? ` · ${s.updated_by}` : ''}` },
            ]} />
            <p className="t-caption muted" style={{ margin: '12px 0 0' }}>
              The credit display scale is a <strong>display transform</strong> only: balances, prices, grants and the ledger stay in
              integer micro-credits. Changing it changes how every screen shows credits — no stored figure moves.
            </p>
          </>
        )}
      </QueryState>
      {editing && settings.data && <SettingsForm env={env} settings={settings.data} onClose={() => setEditing(false)} />}
    </Section>
  );
}

function SettingsForm({ env, settings, onClose }: { env: string; settings: BillingSettings; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [currency, setCurrency] = useState(settings.default_currency);
  const [taxKind, setTaxKind] = useState(settings.default_tax_kind);
  const [taxPercent, setTaxPercent] = useState(String(settings.default_tax_rate_bps / 100));
  const [labelEn, setLabelEn] = useState(settings.tax_label_en);
  const [labelAr, setLabelAr] = useState(settings.tax_label_ar);
  const [exTax, setExTax] = useState(settings.prices_ex_tax);
  const [prefix, setPrefix] = useState(settings.invoice_prefix);
  const [scale, setScale] = useState(String(settings.credit_display_scale));
  const [dunning, setDunning] = useState(settings.dunning_schedule_days.join(', '));
  const [purchase, setPurchase] = useState<PurchaseSettingsInput>(() => purchaseSettingsInput(settings));
  const [reason, setReason] = useState('');
  const purchaseCheck = purchaseSettingsPatch(settings, purchase);

  // Number('') is 0: an emptied field must be refused, never saved as a 0 % tax.
  const taxBps = taxPercent.trim() === '' ? Number.NaN : Math.round(Number(taxPercent) * 100);
  const scaleNumber = Number(scale);
  const dunningDays = dunning.split(',').map((d) => d.trim()).filter(Boolean).map(Number);
  const errors = {
    currency: /^[A-Za-z]{3}$/.test(currency) ? null : 'ISO-4217.',
    tax: taxPercent.trim() === '' ? 'Enter a tax rate (0 for none).' : Number.isFinite(taxBps) && taxBps >= 0 && taxBps <= 10000 ? null : '0–100 %.',
    scale: scaleProblem(scale),
    fee: purchaseCheck.errors.fee,
    minCredits: purchaseCheck.errors.min,
    maxCredits: purchaseCheck.errors.max,
    dunning:
      dunningDays.length >= 1 && dunningDays.length <= 10 &&
      new Set(dunningDays).size === dunningDays.length &&
      dunningDays.every((d) => Number.isInteger(d) && d >= 1 && d <= 365)
        ? null
        : '1–10 different days between 1 and 365, comma separated.',
  };

  // Only what changed is sent — billing keeps every null field as it is.
  const body: Record<string, unknown> & { reason: string } = { reason: reason.trim() };
  if (currency.toUpperCase() !== settings.default_currency) body.default_currency = currency.toUpperCase();
  if (taxKind !== settings.default_tax_kind) body.default_tax_kind = taxKind;
  if (taxBps !== settings.default_tax_rate_bps) body.default_tax_rate_bps = taxBps;
  if (labelEn !== settings.tax_label_en) body.tax_label_en = labelEn;
  if (labelAr !== settings.tax_label_ar) body.tax_label_ar = labelAr;
  if (exTax !== settings.prices_ex_tax) body.prices_ex_tax = exTax;
  if (prefix !== settings.invoice_prefix) body.invoice_prefix = prefix;
  if (scaleNumber !== settings.credit_display_scale) body.credit_display_scale = scaleNumber;
  if (dunningDays.join(',') !== settings.dunning_schedule_days.join(',')) body.dunning_schedule_days = dunningDays;
  Object.assign(body, purchaseCheck.patch);
  const changed = Object.keys(body).length > 1;

  const save = useMutation({
    mutationFn: () => billingApi.updateSettings(env, body),
    onSuccess: (updated) => { queryClient.setQueryData(k(env, 'settings'), updated); onClose(); },
  });

  return (
    <Modal title="Edit billing defaults" size="lg" onClose={save.isPending ? undefined : onClose} dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!changed || Object.values(errors).some(Boolean) || !reasonOk(reason) || save.isPending}
            onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save defaults'}</button>
        </>
      }>
      <div className="form-grid">
        <TextField label="Default currency" value={currency} onChange={setCurrency} mono error={errors.currency} />
        <TextField label="Tax kind" value={taxKind} onChange={setTaxKind} />
        <TextField label="Tax rate (%)" value={taxPercent} onChange={setTaxPercent} type="number" step="any" error={errors.tax} />
        <TextField label="Tax label (en)" value={labelEn} onChange={setLabelEn} />
        <TextField label="Tax label (ar)" value={labelAr} onChange={setLabelAr} />
        <TextField label="Invoice prefix" value={prefix} onChange={setPrefix} mono />
        <TextField label="Credit display scale" value={scale} onChange={setScale} type="number" min={1} max={MAX_DISPLAY_SCALE} step={1}
          error={errors.scale}
          hint="Micro-credits per displayed credit, any whole number up to 1,000,000. 2000 = 1 credit is 1 EGP at 50 EGP per USD." />
        <TextField label="Dunning schedule (days)" value={dunning} onChange={setDunning} error={errors.dunning} />
      </div>
      <CheckboxField label="Prices are ex tax" checked={exTax} onChange={setExTax} />
      <h3 className="t-heading" style={{ margin: '16px 0 8px' }}>Credit purchases</h3>
      <div className="form-grid">
        <TextField label="Service fee (%)" value={purchase.feePercent} onChange={(feePercent) => setPurchase({ ...purchase, feePercent })}
          type="number" step="any" min={0} max={100} error={errors.fee}
          hint={purchaseCheck.patch.purchase_service_fee_bps !== undefined
            ? `Saved as ${purchaseCheck.patch.purchase_service_fee_bps} basis points. Added to every credit purchase, then taxed.`
            : 'Added to every credit purchase (packs and custom amounts), then taxed.'} />
        <TextField label="Minimum credits per purchase" value={purchase.minCredits} onChange={(minCredits) => setPurchase({ ...purchase, minCredits })}
          type="number" min={1} step={1} error={errors.minCredits} hint="Displayed credits, for a custom amount." />
        <TextField label="Maximum credits per purchase" value={purchase.maxCredits} onChange={(maxCredits) => setPurchase({ ...purchase, maxCredits })}
          type="number" min={1} step={1} error={errors.maxCredits} hint="Displayed credits, for a custom amount." />
      </div>
      {scaleNumber !== settings.credit_display_scale && !errors.scale && (
        <div className="callout callout-warning" role="status">
          <span className="callout-icon" aria-hidden="true">!</span>
          <div className="t-body">
            Every console and tenant screen will show credits ÷ {scaleNumber.toLocaleString('en-US')} instead of ÷ {scaleText(settings.credit_display_scale)}.
            Balances and prices themselves do not change.
          </div>
        </div>
      )}
      <ReasonField value={reason} onChange={setReason} />
      <MutationError error={save.error} service="billing" />
    </Modal>
  );
}

// ─── FX rates ─────────────────────────────────────────────────────────────

function FxRatesPanel({ env }: { env: string }) {
  const queryClient = useQueryClient();
  const fx = useQuery({ queryKey: k(env, 'fx'), queryFn: () => billingApi.fxRates(env), ...LIVE });
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const [adding, setAdding] = useState(false);
  const [currency, setCurrency] = useState('EGP');
  const [rate, setRate] = useState('');
  const [effective, setEffective] = useState('');
  const [needsReview, setNeedsReview] = useState(false);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');

  const rateNumber = Number(rate);
  const valid = /^[A-Za-z]{3}$/.test(currency) && Number.isFinite(rateNumber) && rateNumber > 0 && reasonOk(reason);
  const add = useMutation({
    mutationFn: () => billingApi.addFxRate(env, {
      currency: currency.toUpperCase(),
      units_per_reference: rateNumber,
      effective_from: localInputToIso(effective) ?? undefined,
      needs_review: needsReview,
      note: note.trim() || undefined,
      reason: reason.trim(),
    }),
    onSuccess: () => { setAdding(false); setRate(''); setReason(''); setNote(''); queryClient.invalidateQueries({ queryKey: k(env, 'fx') }); },
  });

  return (
    <Section title="FX rates" meta="convert cost cards into micro-credits — data, never a constant"
      actions={canManage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdding(true)}>Add rate</button>} flush>
      <QueryState query={fx} service="billing">
        {(data) => (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Currency</th><th className="num">Units per {data.reference_currency}</th><th>Effective from</th><th>Review</th><th>Note</th><th>Added</th></tr></thead>
              <tbody>
                {data.fx_rates.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.currency}</td>
                    <td className="num">{r.units_per_reference.toLocaleString('en-US', { maximumFractionDigits: 6 })}</td>
                    <td className="t-caption">{formatTimestamp(r.effective_from)}</td>
                    <td>{r.needs_review ? <StatusChip status="needs review" tone="warning" /> : <StatusChip status="ok" tone="muted" />}</td>
                    <td className="t-caption">{r.note ?? '—'}</td>
                    <td className="t-caption">{formatTimestamp(r.created_at)}<div className="muted">{r.created_by ?? ''}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      {adding && (
        <Modal title="Add an FX rate" onClose={add.isPending ? undefined : () => setAdding(false)} dismissible={!add.isPending}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)} disabled={add.isPending}>Cancel</button>
              <button type="button" className="btn btn-primary" disabled={!valid || add.isPending} onClick={() => add.mutate()}>{add.isPending ? 'Saving…' : 'Add rate'}</button>
            </>
          }>
          <div className="form-grid">
            <TextField label="Currency" value={currency} onChange={setCurrency} mono />
            <TextField label={`Units per ${fx.data?.reference_currency ?? 'USD'}`} value={rate} onChange={setRate} type="number" step="any" min={0} />
            <TextField label="Effective from (your local time)" value={effective} onChange={setEffective} type="datetime-local" hint="Empty = now." />
          </div>
          <CheckboxField label="Needs review" checked={needsReview} onChange={setNeedsReview} />
          <TextField label="Note" value={note} onChange={setNote} />
          <ReasonField value={reason} onChange={setReason} />
          <MutationError error={add.error} service="billing" />
        </Modal>
      )}
    </Section>
  );
}

// ─── Pricing rules ────────────────────────────────────────────────────────

function PricingRulesPanel({ env }: { env: string }) {
  const queryClient = useQueryClient();
  const rules = useQuery({ queryKey: k(env, 'rules'), queryFn: () => billingApi.pricingRules(env), ...LIVE });
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const [editing, setEditing] = useState<PricingRule | null>(null);
  const [percent, setPercent] = useState('');
  const [reason, setReason] = useState('');
  // Number('') is 0: an emptied markup must be refused, never saved as "sell at cost".
  const bps = percent.trim() === '' ? Number.NaN : Math.round(Number(percent) * 100);
  const markupError = percent.trim() === '' ? 'Enter a markup (0 sells at cost).' : !Number.isFinite(bps) || bps < 0 ? 'A non-negative percentage.' : null;
  const save = useMutation({
    mutationFn: () => billingApi.updatePricingRule(env, editing!.product, bps, reason.trim()),
    onSuccess: () => { setEditing(null); setReason(''); queryClient.invalidateQueries({ queryKey: k(env, 'rules') }); },
  });

  return (
    <Section title="Pricing rules" meta="default markup over cost for consumption prices (price = ceil(cost × (1 + markup)))" flush>
      <QueryState query={rules} service="billing">
        {(data) => (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Product</th><th className="num">Default markup</th><th>Updated</th>{canManage && <th aria-label="Actions" />}</tr></thead>
              <tbody>
                {data.pricing_rules.map((r) => (
                  <tr key={r.product}>
                    <td className="mono">{r.product}</td>
                    <td className="num">{formatBps(r.default_markup_bps)} <span className="t-caption muted">({r.default_markup_bps} bps)</span></td>
                    <td className="t-caption">{formatTimestamp(r.updated_at)}<div className="muted">{r.updated_by ?? ''}</div></td>
                    {canManage && (
                      <td><button type="button" className="btn btn-ghost btn-sm" onClick={() => { setEditing(r); setPercent(String(r.default_markup_bps / 100)); }}>Edit</button></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      {editing && (
        <ConfirmDialog title={`Markup for ${editing.product}`} confirmLabel="Save markup" pending={save.isPending}
          confirmDisabled={markupError !== null || !reasonOk(reason)}
          onConfirm={() => save.mutate()} onCancel={() => { setEditing(null); save.reset(); }}>
          <TextField label="Markup (%)" value={percent} onChange={setPercent} type="number" step="any" min={0} error={markupError}
            hint={Number.isFinite(bps) ? `${bps} bps. Takes effect at once for every type or route without a price-book row (shown as "cost + markup"); published price-book rows do not move.` : undefined} />
          <ReasonField value={reason} onChange={setReason} />
          <MutationError error={save.error} service="billing" />
        </ConfirmDialog>
      )}
    </Section>
  );
}

// ─── Cost cards ───────────────────────────────────────────────────────────

/**
 * Billing's cost-card vocabulary as shipped (`CostUnits.ForProduct` + `ValidateCategory`) — used only when billing does not
 * answer `GET cost-cards/vocabulary` (an older billing), and to split a flat category list per unit (an SMS part takes a
 * PURPOSE, a provider verification a CHANNEL).
 */
export const FALLBACK_VOCABULARY: CostCardVocabulary = {
  products: [
    {
      product: 'sms',
      units: ['part', 'verification'],
      categories: ['transactional', 'marketing', 'otp', 'sms', 'whatsapp'],
      categories_by_unit: { part: ['transactional', 'marketing', 'otp'], verification: ['sms', 'whatsapp'] },
    },
    { product: 'whatsapp', units: ['message'], categories: ['marketing', 'utility', 'authentication', 'service'] },
    { product: 'payments', units: ['intent'], categories: [] },
    { product: 'storage', units: ['gb_month'], categories: [] },
  ],
};

/** The categories a cost card of (product, unit) may carry, `*` (any) first. */
export function costCardCategories(vocabulary: CostCardVocabulary, product: string, unit: string): string[] {
  const entry = vocabulary.products.find((p) => p.product === product);
  if (!entry) return ['*'];
  const byUnit = entry.categories_by_unit?.[unit];
  if (byUnit) return ['*', ...byUnit];
  const fallback = FALLBACK_VOCABULARY.products.find((p) => p.product === product)?.categories_by_unit?.[unit];
  const narrowed = fallback ? entry.categories.filter((c) => fallback.includes(c)) : entry.categories;
  return ['*', ...(narrowed.length > 0 ? narrowed : entry.categories)];
}

/**
 * A cost typed in MAJOR units with up to 6 decimals (`0.0036` — a WhatsApp or SMS cost is often below one cent), sent to
 * billing verbatim as the decimal string `cost_amount` (C10): no float ever touches it.
 */
export function parseCostAmount(input: string): { ok: true; amount: string } | { ok: false; error: string } {
  const text = input.trim().replace(/,/g, '');
  if (text === '') return { ok: false, error: 'Enter the cost.' };
  const match = /^(\d{1,12})(?:\.(\d+))?$/.exec(text);
  if (!match) return { ok: false, error: 'A non-negative amount, e.g. 0.0036.' };
  if ((match[2] ?? '').length > 6) return { ok: false, error: 'At most 6 decimal places.' };
  const whole = match[1]!.replace(/^0+(?=\d)/, '');
  const fraction = (match[2] ?? '').replace(/0+$/, '');
  return { ok: true, amount: fraction ? `${whole}.${fraction}` : whole };
}

/** A card's cost as billing holds it: the exact decimal when billing sends it, else the (rounded) minor units. */
function costText(card: CostCard): string {
  if (card.cost_amount !== undefined && card.cost_amount !== null && card.cost_amount !== '') return `${card.cost_amount} ${card.cost_currency}`;
  return formatMinor(card.cost_amount_minor, card.cost_currency);
}

function useCostCardVocabulary(env: string): { vocabulary: CostCardVocabulary; fallback: boolean; loading: boolean } {
  const query = useQuery({
    queryKey: k(env, 'cost-card-vocabulary'),
    queryFn: () => billingApi.costCardVocabulary(env),
    staleTime: 5 * 60_000,
    retry: false,
  });
  if (query.data?.products?.length) return { vocabulary: query.data, fallback: false, loading: false };
  return { vocabulary: FALLBACK_VOCABULARY, fallback: query.isError, loading: query.isPending };
}

function CostCardsPanel({ env }: { env: string }) {
  const [product, setProduct] = useState('');
  const [provider, setProvider] = useState('');
  const [country, setCountry] = useState('');
  const [effectiveOnly, setEffectiveOnly] = useState(true);
  const [adding, setAdding] = useState(false);
  const { vocabulary } = useCostCardVocabulary(env);
  const filter = useMemo(() => ({
    product: product || undefined,
    provider_key: provider.trim() || undefined,
    country: country.trim().toUpperCase() || undefined,
    effective_only: effectiveOnly,
  }), [product, provider, country, effectiveOnly]);
  const cards = useQuery({ queryKey: k(env, 'cost-cards', JSON.stringify(filter)), queryFn: () => billingApi.costCards(env, filter), ...LIVE });
  const { scale } = useCreditScale(env);

  return (
    <Section title="Cost cards" meta="what providers charge us — add-only: a new row supersedes, nothing is edited"
      actions={hasPermission(PERMISSIONS.billingManage) && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdding(true)}>Add cost card</button>} flush>
      <div className="card-body">
        <div className="filter-grid">
          <Select label="Product" value={product} onChange={setProduct} placeholder="All products" searchable={false}
            options={vocabulary.products.map((p) => ({ value: p.product, label: p.product }))} />
          <TextField label="Provider" value={provider} onChange={setProvider} mono placeholder="cequens" />
          <TextField label="Country" value={country} onChange={setCountry} mono placeholder="EG" />
          <CheckboxField label="Effective rows only" checked={effectiveOnly} onChange={setEffectiveOnly} />
        </div>
      </div>
      <QueryState query={cards} service="billing">
        {(data) => data.cost_cards.length === 0 ? (
          <div className="empty-state t-body">No cost cards — a route without one is unpriced and blocked (SMS_ROUTE_UNPRICED), never sent at an unknown cost.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Product · provider</th><th>Category · country · network</th><th className="num">Cost</th><th className="num">In credits</th><th>Validity</th><th>State</th></tr></thead>
              <tbody>
                {data.cost_cards.map((c: CostCard) => (
                  <tr key={c.id} className={c.effective ? undefined : 'row-muted'}>
                    <td className="mono">{c.product}<div className="t-caption muted">{c.provider_key}</div></td>
                    <td className="mono t-caption">{c.category} · {c.country} · {c.network}<div className="muted">per {c.unit}</div></td>
                    <td className="num">{costText(c)}</td>
                    <td className="num">{c.cost_credits === null ? <span className="subtle" title="No FX rate for this currency">no FX rate</span> : formatCredits(c.cost_credits, scale)}</td>
                    <td className="t-caption">{formatTimestamp(c.valid_from)} → {c.valid_to ? formatTimestamp(c.valid_to) : 'open'}<div className="muted">v{c.version}{c.note ? ` · ${c.note}` : ''}</div></td>
                    <td>{c.effective ? <StatusChip status="effective" tone="success" /> : <StatusChip status="superseded" tone="muted" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      {adding && <CostCardForm env={env} onClose={() => setAdding(false)} />}
    </Section>
  );
}

export function CostCardForm({ env, onClose }: { env: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { vocabulary, fallback } = useCostCardVocabulary(env);
  const [product, setProduct] = useState('sms');
  const [provider, setProvider] = useState('cequens');
  const [unit, setUnit] = useState('part');
  const [category, setCategory] = useState('*');
  const [country, setCountry] = useState('EG');
  const [network, setNetwork] = useState('*');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EGP');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');

  const units = vocabulary.products.find((p) => p.product === product)?.units ?? [];
  const categories = costCardCategories(vocabulary, product, unit);
  // The product drives the unit, the unit drives the category: a change resets what no longer applies.
  const chooseProduct = (next: string) => {
    const nextUnits = vocabulary.products.find((p) => p.product === next)?.units ?? [];
    const nextUnit = nextUnits[0] ?? '';
    setProduct(next);
    setUnit(nextUnit);
    setCategory('*');
  };
  const chooseUnit = (next: string) => {
    setUnit(next);
    if (!costCardCategories(vocabulary, product, next).includes(category)) setCategory('*');
  };

  const parsed = parseCostAmount(amount);
  const valid = provider.trim() !== '' && units.includes(unit) && categories.includes(category) && parsed.ok &&
    /^[A-Za-z]{3}$/.test(currency) && reasonOk(reason);

  const add = useMutation({
    mutationFn: () => billingApi.addCostCard(env, {
      product, provider_key: provider.trim(), category, country: country.trim().toUpperCase() || '*',
      network: network.trim() || '*', unit, cost_amount: parsed.ok ? parsed.amount : undefined, cost_currency: currency.toUpperCase(),
      valid_from: localInputToIso(validFrom) ?? undefined, valid_to: localInputToIso(validTo) ?? undefined,
      note: note.trim() || undefined, reason: reason.trim(),
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ops', 'billing', env, 'cost-cards'] }); onClose(); },
  });

  return (
    <Modal title="Add a cost card version" size="lg" onClose={add.isPending ? undefined : onClose} dismissible={!add.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={add.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || add.isPending} onClick={() => add.mutate()}>{add.isPending ? 'Saving…' : 'Add version'}</button>
        </>
      }>
      <p className="t-caption muted" style={{ margin: 0 }}>Entered from the provider's contract or invoice. The newest effective row for a route is the cost.</p>
      {fallback && (
        <p className="t-caption note-error" role="status" style={{ margin: 0 }}>
          Billing did not return its cost-card vocabulary; the console's built-in list is used. Billing re-checks every value.
        </p>
      )}
      <div className="form-grid">
        <Select label="Product" value={product} onChange={chooseProduct} searchable={false}
          options={vocabulary.products.map((p) => ({ value: p.product, label: p.product }))} />
        <TextField label="Provider key" value={provider} onChange={setProvider} mono />
        <Select label="Unit" value={unit} onChange={chooseUnit} searchable={false} options={units.map((u) => ({ value: u, label: u }))} />
        <Select label={product === 'sms' && unit === 'verification' ? 'Channel' : product === 'sms' ? 'Purpose' : 'Category'} value={category}
          onChange={setCategory} searchable={false}
          options={categories.map((c) => ({ value: c, label: c === '*' ? '* (any)' : c }))} />
        <TextField label="Country" value={country} onChange={setCountry} mono hint="ISO-2, or * for any." />
        <TextField label="Network" value={network} onChange={setNetwork} mono hint="* for any." />
        <TextField label={`Cost per ${unit || 'unit'} (${currency.toUpperCase()})`} value={amount} onChange={setAmount}
          error={amount && !parsed.ok ? parsed.error : null}
          hint="In the currency's major units, up to 6 decimals (0.0036) — sub-cent costs are kept exactly." />
        <TextField label="Currency" value={currency} onChange={setCurrency} mono />
        <TextField label="Valid from (your local time)" value={validFrom} onChange={setValidFrom} type="datetime-local" hint="Empty = now." />
        <TextField label="Valid to (your local time)" value={validTo} onChange={setValidTo} type="datetime-local" hint="Empty = open." />
      </div>
      <TextField label="Note" value={note} onChange={setNote} />
      <ReasonField value={reason} onChange={setReason} />
      <MutationError error={add.error} service="billing" />
    </Modal>
  );
}
